import { useSyncExternalStore } from 'react';

/**
 * Melodía de bienvenida del QR de registro (E1-26): quien entra por `/?registro=1&origen=qr` ve en
 * el registro la alerta "Escúchanos", y al tocarla suena "El Parche Cafetero" en bucle: sigue sonando
 * (también mientras sale a WhatsApp por el PIN) hasta que cierre la página o toque "Silenciar".
 *
 * Si no la tocó, suena al terminar de iniciar sesión. El navegador solo deja sonar audio tras un
 * gesto y el código se verifica después de un `await`, así que el audio se desbloquea en el toque de
 * "Recibir PIN" (`primeWelcomeSong`) y suena al confirmar la sesión (`startWelcomeSong`). Si el
 * navegador recargó la página al volver de WhatsApp, el desbloqueo se pierde y el control ofrece
 * "Escuchar".
 *
 * Vive en el módulo (no en un componente) porque ningún layout de cliente sobrevive de la landing
 * al mapa.
 */
export const WELCOME_SONG_URL = '/audio/bienvenida-parche-cafetero.mp3';

/** Marca "entró por el QR" en `sessionStorage`: sobrevive a la recarga al volver de WhatsApp. */
const QR_ORIGIN_KEY = 'parche-cafetero:bienvenida-qr';

/** `playing`: sonando · `blocked`: el navegador no la dejó sonar sola y espera un toque. */
export type WelcomeSongState = 'idle' | 'playing' | 'blocked';

type Listener = () => void;

let state: WelcomeSongState = 'idle';
let audio: HTMLAudioElement | null = null;
const listeners = new Set<Listener>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

function setState(next: WelcomeSongState): void {
  if (state === next) return;
  state = next;
  notify();
}

function storage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    // Almacenamiento bloqueado: la marca no se guarda y la melodía no suena
    return null;
  }
}

export function markQrOrigin(): void {
  try {
    storage()?.setItem(QR_ORIGIN_KEY, '1');
  } catch {
    // Sin almacenamiento: no hay melodía
  }
  notify();
}

export function hasQrOrigin(): boolean {
  try {
    return storage()?.getItem(QR_ORIGIN_KEY) === '1';
  } catch {
    return false;
  }
}

export function clearQrOrigin(): void {
  try {
    storage()?.removeItem(QR_ORIGIN_KEY);
  } catch {
    // Nada que limpiar
  }
  notify();
}

function element(): HTMLAudioElement {
  if (!audio) {
    audio = new Audio(WELCOME_SONG_URL);
    // La canción completa pesa ~4 MB: se descarga a medida que suena, no de una vez
    audio.preload = 'metadata';
    audio.loop = true;
  }
  return audio;
}

/**
 * Dentro del gesto (antes de cualquier `await`): carga el audio y lo reproduce en silencio un instante
 * para que el navegador lo deje sonar después. Sin la marca del QR no hace nada.
 */
export function primeWelcomeSong(): void {
  if (!hasQrOrigin() || state === 'playing') return;
  const song = element();
  song.muted = true;
  song.play().then(
    () => {
      if (state !== 'playing') {
        song.pause();
        song.currentTime = 0;
      }
      song.muted = false;
    },
    () => {
      song.muted = false;
    },
  );
}

/** Suena desde el principio. Consume la marca: suena una vez por escaneo. */
export function startWelcomeSong(): void {
  clearQrOrigin();
  const song = element();
  song.muted = false;
  song.currentTime = 0;
  setState('playing');
  song.play().catch(() => {
    if (state === 'playing') setState('blocked');
  });
}

/** "Silenciar" o descartar: se detiene y el control desaparece. */
export function stopWelcomeSong(): void {
  audio?.pause();
  setState('idle');
}

function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const getState = (): WelcomeSongState => state;
const getServerState = (): WelcomeSongState => 'idle';

export function useWelcomeSong(): WelcomeSongState {
  return useSyncExternalStore(subscribe, getState, getServerState);
}

/** Entró por el QR y la melodía aún no sonó: el registro muestra la alerta "Escúchanos". */
export function useQrInvite(): boolean {
  return useSyncExternalStore(subscribe, hasQrOrigin, () => false);
}

/** Solo pruebas: olvida el elemento de audio, el estado y la marca. */
export function resetWelcomeSong(): void {
  audio = null;
  state = 'idle';
  clearQrOrigin();
}
